import React, { useEffect, Suspense, lazy, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Hero } from '../components/Hero';
import { SocialProofSection } from '../components/SocialProofSection';
import { Footer } from '../components/Footer';
import { SEO } from '../components/SEO';
import { useScrollToAnchor } from '../hooks/useScrollToAnchor';
import { useLanguage } from '../contexts/LanguageContext';
// Lazy load below-the-fold components
const FeatureShowcase = lazy(() => import('../components/FeatureShowcase').then(m => ({
  default: m.FeatureShowcase
})));
const PriorisierungSection = lazy(() => import('../components/PriorisierungSection').then(m => ({
  default: m.PriorisierungSection
})));
const StatsSection = lazy(() => import('../components/StatsSection').then(m => ({
  default: m.StatsSection
})));
const WasIstSection = lazy(() => import('../components/WasIstSection').then(m => ({
  default: m.WasIstSection
})));
const VorteileSection = lazy(() => import('../components/VorteileSection').then(m => ({
  default: m.VorteileSection
})));
const CapabilitiesSection = lazy(() => import('../components/CapabilitiesSection').then(m => ({
  default: m.CapabilitiesSection
})));
const ComparisonSection = lazy(() => import('../components/ComparisonSection').then(m => ({
  default: m.ComparisonSection
})));
const TestimonialSection = lazy(() => import('../components/TestimonialSection').then(m => ({
  default: m.TestimonialSection
})));
const CTASection = lazy(() => import('../components/CTASection').then(m => ({
  default: m.CTASection
})));
// Loading fallback component
function SectionLoader() {
  return <div className="py-16 lg:py-24 flex items-center justify-center" data-id="element-537">
      <div className="w-8 h-8 border-4 border-[#ff6b35]/20 border-t-[#ff6b35] rounded-full animate-spin" data-id="element-538" />
    </div>;
}
export function HomePage() {
  const {
    language
  } = useLanguage();
  // Handle anchor scrolling after lazy components load
  useScrollToAnchor();
  // Add FAQ Schema
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: language === 'de' ? 'Was ist CRM Kraft?' : 'What is CRM Kraft?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'CRM Kraft ist eine spezialisierte CRM-Beratung, die Unternehmen dabei hilft, ihre Vertriebseffizienz durch intelligente CRM-Systeme zu verdoppeln. Wir bieten KI-gestützte Lead-Priorisierung, automatisiertes Follow-up und intelligente Verkaufsprognosen.' : 'CRM Kraft is a specialized CRM consultancy that helps companies double their sales efficiency through intelligent CRM systems. We offer AI-powered lead prioritization, automated follow-up, and intelligent sales forecasts.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Welche CRM-Systeme unterstützt CRM Kraft?' : 'Which CRM systems does CRM Kraft support?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Wir sind offizieller Close Partner und spezialisieren uns auf Close CRM. Wir haben über 110 CRM-Systeme erfolgreich implementiert und arbeiten mit über 1.200 Vertriebsprofis zusammen.' : 'We are an official Close Partner and specialize in Close CRM. We have successfully implemented over 110 CRM systems and work with over 1,200 sales professionals.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Wie viel Umsatz kann ich mit CRM Kraft steigern?' : 'How much revenue can I increase with CRM Kraft?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Unsere Kunden haben zusammen über €7 Millionen zusätzlichen Umsatz generiert. Durch KI-gestützte Lead-Priorisierung, automatisiertes Follow-up und intelligente Prozesse können Sie Ihre Vertriebseffizienz signifikant steigern.' : 'Our clients have collectively generated over €7 million in additional revenue. Through AI-powered lead prioritization, automated follow-up, and intelligent processes, you can significantly increase your sales efficiency.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Wie lange dauert die CRM-Implementierung?' : 'How long does CRM implementation take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Die Implementierung erfolgt in 3 Schritten: 1) Analyse & Strategie (1-2 Wochen), 2) Setup & Integration (2-4 Wochen), 3) Training & Optimierung (laufend). Die meisten Kunden sehen erste Ergebnisse bereits nach 4-6 Wochen.' : 'Implementation happens in 3 steps: 1) Analysis & Strategy (1-2 weeks), 2) Setup & Integration (2-4 weeks), 3) Training & Optimization (ongoing). Most clients see initial results within 4-6 weeks.'
        }
      }, {
        '@type': 'Question',
        name: language === 'de' ? 'Was kostet die Zusammenarbeit mit CRM Kraft?' : 'What does working with CRM Kraft cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: language === 'de' ? 'Die Kosten variieren je nach Umfang und Anforderungen. Wir bieten eine kostenlose Erstberatung an, in der wir Ihre Situation analysieren und ein individuelles Angebot erstellen. Buchen Sie jetzt Ihre kostenlose Demo.' : 'Costs vary depending on scope and requirements. We offer a free initial consultation where we analyze your situation and create a customized offer. Book your free demo now.'
        }
      }]
    };
    let scriptTag = document.querySelector('script[data-faq-schema]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      scriptTag.setAttribute('data-faq-schema', 'true');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(faqSchema);
    return () => {
      const tag = document.querySelector('script[data-faq-schema]');
      if (tag) tag.remove();
    };
  }, [language]);
  return <div className="min-h-screen bg-[#0a0e1a]" data-id="element-539">
      <SEO title={language === 'de' ? 'CRM Kraft - Super-Performance im Vertrieb' : 'CRM Kraft - Super Performance in Sales'} description={language === 'de' ? 'Steigere Deine Vertriebseffizienz mit KI-gestützter Lead-Priorisierung, automatisiertem Follow-up und intelligenten Verkaufsprognosen.' : 'Increase your sales efficiency with AI-powered lead prioritization, automated follow-up, and intelligent sales forecasts.'} canonical="https://crmkraft.de/" data-id="element-540" />
      <Navigation data-id="element-541" />
      <main data-id="element-542">
        {/* Above the fold - load immediately */}
        <Hero data-id="element-543" />
        <SocialProofSection data-id="element-544" />

        {/* Below the fold - lazy load */}
        <Suspense fallback={<SectionLoader data-id="element-546" />} data-id="element-545">
          <FeatureShowcase data-id="element-547" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-549" />} data-id="element-548">
          <PriorisierungSection data-id="element-550" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-552" />} data-id="element-551">
          <StatsSection data-id="element-553" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-555" />} data-id="element-554">
          <WasIstSection data-id="element-556" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-558" />} data-id="element-557">
          <VorteileSection data-id="element-559" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-561" />} data-id="element-560">
          <CapabilitiesSection data-id="element-562" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-564" />} data-id="element-563">
          <ComparisonSection data-id="element-565" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-567" />} data-id="element-566">
          <TestimonialSection data-id="element-568" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-570" />} data-id="element-569">
          <CTASection data-id="element-571" />
        </Suspense>
      </main>
      <Footer data-id="element-572" />
    </div>;
}